import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api';
import { theme, t } from '../theme';
import { useConfirm } from './ConfirmProvider';
import { entregarPdf } from '../platform/arquivos';
import { isNative } from '../platform/native';
import { AuthContext } from '../context/AuthContext';
import { moduloLigado, MODULO_ETIQUETAS } from '../config/modulos';
import EtiquetaModal from './EtiquetaModal';

const FichaTecnicaDetalhes = () => {
  const { tenant } = useContext(AuthContext);
  const temEtiquetas = moduloLigado(tenant, MODULO_ETIQUETAS);
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recalculando, setRecalculando] = useState(false);
  const [mostrarReceita, setMostrarReceita] = useState(false);
  const [etiquetaAberta, setEtiquetaAberta] = useState(false);
  const confirm = useConfirm();

  const rotaLista = (tipo) => tipo === 'preparacao_base' ? '/fichas-tecnicas/base' : tipo === 'produto_final' ? '/fichas-tecnicas/produto' : '/fichas-tecnicas';

  useEffect(() => {
    // ✅ PROTEÇÃO: Verificar se ID é válido
    if (!id || id === 'nova' || id === 'editar' || isNaN(id)) {
      console.warn('ID inválido detectado:', id);
      navigate(-1);
      return;
    }

    carregarFicha();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const carregarFicha = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/fichas/api/fichas-tecnicas/${id}`);
      
      if (response.data.success) {
        setFicha(response.data.ficha);
      } else {
        setError('Ficha técnica não encontrada');
      }
    } catch (err) {
      console.error('Erro ao carregar ficha:', err);
      setError('Erro ao carregar ficha técnica');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalcular = async () => {
    try {
      setRecalculando(true);
      const response = await api.post(`/fichas/api/fichas-tecnicas/${id}/recalcular`);
      
      if (response.data.success) {
        alert('Custos recalculados com sucesso!');
        carregarFicha();
      }
    } catch (err) {
      alert('Erro ao recalcular custos');
      console.error(err);
    } finally {
      setRecalculando(false);
    }
  };

  const handleDeletar = async () => {
    if (!await confirm(`Tem certeza que deseja excluir a ficha técnica "${ficha.nome}"?`, { danger: true, confirmText: 'Excluir' })) {
      return;
    }

    try {
      const response = await api.delete(`/fichas/api/fichas-tecnicas/${id}`);
      
      if (response.data.success) {
        alert('Ficha técnica excluída com sucesso!');
        navigate(rotaLista(ficha?.tipo_ficha));
      }
    } catch (err) {
      alert('Erro ao excluir ficha técnica');
      console.error(err);
    }
  };

  const formatarMoeda = (valor) => {
    if (!valor) return 'R$ 0,00';
    return `R$ ${parseFloat(valor).toFixed(2).replace('.', ',')}`;
  };

  const formatarPercentual = (valor) => {
    if (!valor) return '0%';
    return `${parseFloat(valor).toFixed(1)}%`;
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const exportarPDF = () => {
    if (!ficha) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const marginX = 15;
    const isProdutoFinal = ficha.tipo_ficha === 'produto_final';
    let y = 18;

    // Cabecalho
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(ficha.nome, marginX, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(
      `${isProdutoFinal ? 'Produto Final' : 'Preparacao Base'}${ficha.categoria ? ' · ' + ficha.categoria : ''}`,
      marginX, y
    );
    doc.setTextColor(0);
    y += 8;

    // Informacoes basicas em 2 colunas
    const info = [
      ['Rendimento', `${ficha.rendimento || '-'} ${ficha.unidade_rendimento || ''}`.trim()],
      ['Tempo de preparo', ficha.tempo_preparo_minutos ? `${ficha.tempo_preparo_minutos} min` : '-'],
      ['Peso total', ficha.peso_total ? `${ficha.peso_total} g` : '-'],
      ['Criado em', formatarData(ficha.data_criacao)],
      ['Atualizado em', formatarData(ficha.data_atualizacao)],
      ['Status', ficha.ativo ? 'Ativo' : 'Inativo'],
    ];
    const infoRows = [];
    for (let i = 0; i < info.length; i += 2) {
      infoRows.push([info[i][0], info[i][1], info[i + 1]?.[0] || '', info[i + 1]?.[1] || '']);
    }
    autoTable(doc, {
      startY: y,
      body: infoRows,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: { top: 1, right: 2, bottom: 1, left: 0 } },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 35, textColor: [80, 80, 80] },
        1: { cellWidth: 55 },
        2: { fontStyle: 'bold', cellWidth: 35, textColor: [80, 80, 80] },
        3: { cellWidth: 55 },
      },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 6;

    if (ficha.descricao) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Descricao', marginX, y);
      y += 4;
      doc.setFont('helvetica', 'normal');
      const desc = doc.splitTextToSize(ficha.descricao, pageW - 2 * marginX);
      doc.text(desc, marginX, y);
      y += desc.length * 5 + 3;
    }

    // Ingredientes (insumos + sub-receitas)
    const ingLinhas = [
      ...insumos.map(i => [
        i.nome,
        `${i.quantidade} ${i.unidade_medida || ''}`.trim(),
        formatarMoeda(i.custo_unitario),
        formatarMoeda(i.custo_total),
      ]),
      ...((ficha.sub_receitas || []).map(s => [
        `${s.nome} (Preparacao Base)`,
        `${s.quantidade} ${s.unidade_medida || s.unidade_rendimento || ''}`.trim(),
        formatarMoeda(s.custo_unitario),
        formatarMoeda(s.custo_total),
      ])),
    ];
    if (ingLinhas.length) {
      const subtotal =
        insumos.reduce((s, i) => s + (i.custo_total || 0), 0) +
        (ficha.sub_receitas || []).reduce((s, i) => s + (i.custo_total || 0), 0);
      autoTable(doc, {
        startY: y,
        head: [['Ingredientes', 'Quantidade', 'Custo unitario', 'Custo total']],
        body: ingLinhas,
        foot: [['', '', 'Subtotal', formatarMoeda(subtotal)]],
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [196, 120, 110], textColor: 255, fontStyle: 'bold' },
        footStyles: { fillColor: [245, 230, 227], textColor: 60, fontStyle: 'bold' },
        columnStyles: {
          1: { halign: 'center' },
          2: { halign: 'right' },
          3: { halign: 'right' },
        },
        margin: { left: marginX, right: marginX },
      });
      y = doc.lastAutoTable.finalY + 6;
    }

    // Embalagens
    if (embalagens.length) {
      const subtotalEmb = embalagens.reduce((s, i) => s + (i.custo_total || 0), 0);
      autoTable(doc, {
        startY: y,
        head: [['Embalagens', 'Quantidade', 'Custo unitario', 'Custo total']],
        body: embalagens.map(e => [
          e.nome, e.quantidade, formatarMoeda(e.custo_unitario), formatarMoeda(e.custo_total),
        ]),
        foot: [['', '', 'Subtotal', formatarMoeda(subtotalEmb)]],
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [122, 110, 104], textColor: 255, fontStyle: 'bold' },
        footStyles: { fillColor: [232, 226, 221], textColor: 60, fontStyle: 'bold' },
        columnStyles: {
          1: { halign: 'center' },
          2: { halign: 'right' },
          3: { halign: 'right' },
        },
        margin: { left: marginX, right: marginX },
      });
      y = doc.lastAutoTable.finalY + 6;
    }

    // Custos + Precificacao
    const custoRows = [
      ['Custo total', formatarMoeda(ficha.custo_total)],
      [`Custo por ${ficha.unidade_rendimento || 'unidade'}`, formatarMoeda(ficha.custo_unitario)],
    ];
    if (isProdutoFinal) {
      custoRows.push(
        ['Preco de venda', formatarMoeda(ficha.preco_venda_sugerido)],
        ['Lucro por unidade', formatarMoeda((ficha.preco_venda_sugerido || 0) - (ficha.custo_unitario || 0))],
        ['Markup', ficha.custo_unitario > 0 ? `${(ficha.preco_venda_sugerido / ficha.custo_unitario).toFixed(2)}x` : '-'],
        ['CMV', formatarPercentual(ficha.cmv_percentual)],
        ['Margem de lucro', formatarPercentual(ficha.margem_lucro_percentual)],
      );
    }
    autoTable(doc, {
      startY: y,
      head: [[isProdutoFinal ? 'Custos e precificacao' : 'Custos', '']],
      body: custoRows,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [196, 120, 110], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 90, textColor: [80, 80, 80] },
        1: { halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: marginX, right: marginX },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Modo de preparo
    if (ficha.modo_preparo) {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Modo de preparo', marginX, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const passos = doc.splitTextToSize(ficha.modo_preparo, pageW - 2 * marginX);
      // Quebra pagina se nao couber
      const alturaTexto = passos.length * 5;
      if (y + alturaTexto > 285) { doc.addPage(); y = 20; }
      doc.text(passos, marginX, y);
    }

    // Rodape em todas as paginas
    const totalPaginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPaginas; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150);
      const emitido = new Date().toLocaleDateString('pt-BR');
      doc.text(`Ficha tecnica gerada pelo Metria em ${emitido}`, marginX, 292);
      doc.text(`Pagina ${p} de ${totalPaginas}`, pageW - marginX, 292, { align: 'right' });
    }

    // Slug do arquivo
    const slug = (ficha.nome || 'ficha').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return avisarSeFalhar(entregarPdf(doc, `ficha-${slug}.pdf`));
  };

  /**
   * Um export que falha calado e indistinguivel de um botao quebrado — foi
   * assim que o "nao acontece nada" passou despercebido ate o teste no
   * aparelho. Qualquer erro daqui vira mensagem na tela.
   */
  const avisarSeFalhar = async (promessa) => {
    try {
      const ok = await promessa;
      if (!ok) alert('Não consegui gerar o arquivo neste aparelho.');
      return ok;
    } catch (e) {
      alert('Não consegui gerar o arquivo: ' + ((e && e.message) || 'erro desconhecido'));
      return false;
    }
  };

  /**
   * Receita multiplicada em PDF — a mesma tabela da tela.
   *
   * No app nao da para usar window.print(): o WKWebView nao implementa impressao
   * do documento (a funcao nem existe), e o toque nao fazia nada.
   */
  const exportarReceitaMultiplicadaPDF = () => {
    if (!ficha) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`${ficha.nome} — Receita Multiplicada`, 14, 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Rendimento: ${ficha.rendimento || '-'} ${ficha.unidade_rendimento || ''}`
      + (ficha.peso_total ? ` (${ficha.peso_total}g)` : ''), 14, 22);
    doc.setTextColor(0);

    const cabecalho = ['Ingrediente', ...multiplicadores.map(m => (m === 0.5 ? '1/2x' : `${m}x`))];
    const qtd = (valor, m, inteiro) => {
      const n = valor * m;
      if (inteiro) return String(Math.ceil(n));
      return n % 1 === 0 ? String(n) : n.toFixed(2).replace('.', ',');
    };
    const corpo = [
      ...todosIngredientes.map(item => [
        item.nome,
        ...multiplicadores.map(m => `${qtd(item.quantidade, m)} ${item.unidade_medida || ''}`.trim()),
      ]),
      ...(embalagensFicha.length
        ? [[{ content: 'Embalagens', colSpan: multiplicadores.length + 1, styles: { fontStyle: 'bold', halign: 'center' } }]]
        : []),
      ...embalagensFicha.map(item => [
        item.nome,
        ...multiplicadores.map(m => qtd(item.quantidade, m, true)),
      ]),
    ];

    autoTable(doc, { head: [cabecalho], body: corpo, startY: 28, styles: { fontSize: 9 } });

    if (ficha.modo_preparo) {
      const yFinal = (doc.lastAutoTable?.finalY || 28) + 8;
      doc.setFont('helvetica', 'bold');
      doc.text('Modo de preparo', 14, yFinal);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(doc.splitTextToSize(ficha.modo_preparo, 265), 14, yFinal + 5);
    }

    const slugMult = (ficha.nome || 'ficha').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return avisarSeFalhar(entregarPdf(doc, `receita-${slugMult}.pdf`));
  };

  /**
   * Botao "Imprimir": no navegador abre a impressao da pagina; no app gera o
   * PDF e abre a folha de compartilhamento, de onde sai a impressao do sistema.
   */
  const imprimir = (pdfDoApp) => () => {
    if (isNative() || typeof window.print !== 'function') return pdfDoApp();
    window.print();
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ficha) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          {error || 'Ficha técnica não encontrada'}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          ← Voltar
        </button>
      </div>
    );
  }

  // Separar itens por tipo
  const insumos = ficha.itens?.filter(i => i.tipo === 'insumo') || [];
  const embalagens = ficha.itens?.filter(i => i.tipo === 'embalagem') || [];
  const isProdutoFinal = ficha.tipo_ficha === 'produto_final';


// Dados para receita multiplicada
const multiplicadores = [1, 2, 3, 4, 5, 6, 0.5];
const todosIngredientes = ficha ? [
  ...ficha.itens.filter(i => i.tipo === 'insumo'),
  ...(ficha.sub_receitas || []).map(sub => ({
    nome: `🍰 ${sub.nome}`,
    quantidade: sub.quantidade,
    unidade_medida: sub.unidade_medida || 'porção',
    tipo: 'sub_receita'
  }))
] : [];
const embalagensFicha = ficha ? ficha.itens.filter(i => i.tipo === 'embalagem') : [];

return (
  <div style={{ maxWidth: 1000, margin: '0 auto', fontFamily: theme.font }}>
    <style>{`
      @media print { .modo-preparo-content { font-size: 12px; } }
      .table th { font-size: 12px !important; text-transform: uppercase !important; letter-spacing: 0.05em !important; color: ${theme.colors.textMuted} !important; border-bottom: 1.5px solid ${theme.colors.border} !important; }
      .table td { border-bottom: 1px solid ${theme.colors.borderLight} !important; color: ${theme.colors.text} !important; }
      .table-light { background: ${theme.colors.bgSection} !important; }
      .table-warning { background: ${theme.colors.warningLight} !important; }
    `}</style>
    
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={t.h1}>
            {isProdutoFinal ? '🎂' : '🧈'} {ficha.nome}
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <span style={t.badge(isProdutoFinal ? theme.colors.success : theme.colors.warning, isProdutoFinal ? theme.colors.successLight : theme.colors.warningLight)}>
              {isProdutoFinal ? 'Produto Final' : 'Preparação Base'}
            </span>
            {ficha.categoria && (
              <span style={t.badge(theme.colors.textSecondary, theme.colors.bgSection)}>{ficha.categoria}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={t.btnSecondary} onClick={() => navigate(rotaLista(ficha?.tipo_ficha))}>
            ← Voltar
          </button>
          <button style={t.btnPrimary} onClick={() => navigate(`/fichas-tecnicas/${id}/editar`)}>
            ✏️ Editar
          </button>
          <button style={{ ...t.btnSecondary, borderColor: theme.colors.info, color: theme.colors.info }} onClick={handleRecalcular} disabled={recalculando}>
            {recalculando ? '⏳' : '🔄'} Recalcular
          </button>
          <button style={t.btnDanger} onClick={handleDeletar}>
            🗑️ Excluir
          </button>
        </div>
      </div>

      {/* Informações Básicas */}
      <div style={{ ...t.card, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${theme.colors.borderLight}` }}>
          <h5 style={t.h3}>📝 Informações</h5>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 32px' }}>
            <p style={{ margin: 0, fontSize: 14, color: theme.colors.text }}><strong style={{ color: theme.colors.textSecondary }}>Criado em:</strong> {formatarData(ficha.data_criacao)}</p>
            <p style={{ margin: 0, fontSize: 14, color: theme.colors.text }}><strong style={{ color: theme.colors.textSecondary }}>Rendimento:</strong> {ficha.rendimento} {ficha.unidade_rendimento}</p>
            <p style={{ margin: 0, fontSize: 14, color: theme.colors.text }}><strong style={{ color: theme.colors.textSecondary }}>Atualizado em:</strong> {formatarData(ficha.data_atualizacao)}</p>
            {ficha.peso_total && (
              <p style={{ margin: 0, fontSize: 14, color: theme.colors.text }}><strong style={{ color: theme.colors.textSecondary }}>Peso Total:</strong> {ficha.peso_total}g</p>
            )}
            <p style={{ margin: 0, fontSize: 14, color: theme.colors.text }}><strong style={{ color: theme.colors.textSecondary }}>Tempo de Preparo:</strong> {ficha.tempo_preparo_minutos ? `${ficha.tempo_preparo_minutos} minutos` : '-'}</p>
            <p style={{ margin: 0, fontSize: 14, color: theme.colors.text }}><strong style={{ color: theme.colors.textSecondary }}>Status:</strong> <span style={t.badge(ficha.ativo ? theme.colors.success : theme.colors.danger, ficha.ativo ? theme.colors.successLight : theme.colors.dangerLight)}>{ficha.ativo ? 'Ativo' : 'Inativo'}</span></p>
          </div>
          {ficha.descricao && (
            <div style={{ marginTop: 12 }}>
              <strong style={{ color: theme.colors.textSecondary, fontSize: 14 }}>Descrição:</strong>
              <p style={{ margin: '4px 0 0', fontSize: 14, color: theme.colors.text }}>{ficha.descricao}</p>
            </div>
          )}
        </div>
      </div>

      {/* Ingredientes (Insumos + Preparações Base) */}
      {(insumos.length > 0 || (ficha.sub_receitas && ficha.sub_receitas.length > 0)) && (
        <div style={{ ...t.card, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', background: theme.colors.primaryLight, borderBottom: `1px solid ${theme.colors.border}` }}>
            <h5 style={{ ...t.h3, color: theme.colors.primaryDark }}>🥄 Ingredientes ({insumos.length + (ficha.sub_receitas?.length || 0)})</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="text-center">Quantidade</th>
                    <th className="text-center">Unidade</th>
                    <th className="text-end">Custo Unitário</th>
                    <th className="text-end">Custo Total</th>
                    <th>Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {insumos.map((item, index) => (
                    <tr key={`i-${index}`}>
                      <td><strong>{item.nome}</strong></td>
                      <td className="text-center">{item.quantidade}</td>
                      <td className="text-center">
                        {item.unidade_medida}
                        {item.convertido && (
                          <span title="Unidade convertida automaticamente na importação (aproximado) — confira e ajuste se precisar"
                            style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#8a6d1f', background: '#fff3d6',
                              border: '1px solid #f0d68a', padding: '1px 6px', borderRadius: 8, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            convertido
                          </span>
                        )}
                      </td>
                      <td className="text-end">{formatarMoeda(item.custo_unitario)}</td>
                      <td className="text-end"><strong>{formatarMoeda(item.custo_total)}</strong></td>
                      <td>{item.observacoes && <small className="text-muted">{item.observacoes}</small>}</td>
                    </tr>
                  ))}
                  {(ficha.sub_receitas || []).map((sub, index) => (
                    <tr key={`s-${index}`} style={{ background: theme.colors.warningLight }}>
                      <td>
                        <strong>🍰 {sub.nome}</strong> <span style={{ fontSize: 10, color: theme.colors.warning, fontWeight: 600 }}>Preparação Base</span>
                        {!sub.custo_filha && (
                          <span title="Este preparo ainda não tem os ingredientes cadastrados — abra a ficha dele pra completar e o custo entra aqui."
                            style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#8a6d1f', background: '#fff3d6',
                              border: '1px solid #f0d68a', padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap' }}>
                            a completar
                          </span>
                        )}
                      </td>
                      <td className="text-center">{sub.quantidade}</td>
                      <td className="text-center">{sub.unidade_medida || sub.unidade_rendimento}</td>
                      <td className="text-end">{formatarMoeda(sub.custo_unitario)}</td>
                      <td className="text-end"><strong>{formatarMoeda(sub.custo_total)}</strong></td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light fw-bold">
                    <td colSpan="4" className="text-end">Subtotal Ingredientes:</td>
                    <td className="text-end text-primary">
                      {formatarMoeda(
                        insumos.reduce((sum, i) => sum + (i.custo_total || 0), 0) +
                        (ficha.sub_receitas || []).reduce((sum, s) => sum + (s.custo_total || 0), 0)
                      )}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Embalagens */}
      {embalagens.length > 0 && (
        <div style={{ ...t.card, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', background: theme.colors.bgSection, borderBottom: `1px solid ${theme.colors.border}` }}>
            <h5 style={{ ...t.h3, color: theme.colors.secondary }}>📦 Embalagens ({embalagens.length})</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="text-center">Quantidade</th>
                    <th className="text-end">Custo Unitário</th>
                    <th className="text-end">Custo Total</th>
                    <th>Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {embalagens.map((item, index) => (
                    <tr key={index}>
                      <td><strong>{item.nome}</strong></td>
                      <td className="text-center">{item.quantidade}</td>
                      <td className="text-end">{formatarMoeda(item.custo_unitario)}</td>
                      <td className="text-end">
                        <strong>{formatarMoeda(item.custo_total)}</strong>
                      </td>
                      <td>
                        {item.observacoes && (
                          <small className="text-muted">{item.observacoes}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-light fw-bold">
                    <td colSpan="3" className="text-end">Subtotal Embalagens:</td>
                    <td className="text-end text-secondary">
                      {formatarMoeda(embalagens.reduce((sum, i) => sum + (i.custo_total || 0), 0))}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Resumo de Custos */}
      <div style={{ ...t.card, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', background: theme.colors.primaryLight, borderBottom: `1px solid ${theme.colors.border}` }}>
          <h5 style={{ ...t.h3, color: theme.colors.primaryDark }}>💰 Custos</h5>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ ...t.section, textAlign: 'center' }}>
              <small style={{ color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Custo Total</small>
              <span style={{ fontSize: 22, fontWeight: 700, color: theme.colors.primary }}>{formatarMoeda(ficha.custo_total)}</span>
            </div>
            <div style={{ ...t.section, textAlign: 'center' }}>
              <small style={{ color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Custo por {ficha.unidade_rendimento}</small>
              <span style={{ fontSize: 22, fontWeight: 700, color: theme.colors.info }}>{formatarMoeda(ficha.custo_unitario)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Precificação - SOMENTE PARA PRODUTO FINAL */}
      {isProdutoFinal && (
        <div style={{ ...t.card, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', background: theme.colors.successLight, borderBottom: `1px solid ${theme.colors.border}` }}>
            <h5 style={{ ...t.h3, color: theme.colors.success }}>💰 Precificação</h5>
          </div>
          <div style={{ padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
              <div style={{ ...t.section, textAlign: 'center' }}>
                <small style={{ color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Preço de Venda</small>
                <span style={{ fontSize: 20, fontWeight: 700, color: theme.colors.success }}>{formatarMoeda(ficha.preco_venda_sugerido)}</span>
              </div>
              <div style={{ ...t.section, textAlign: 'center' }}>
                <small style={{ color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Lucro por Unidade</small>
                <span style={{ fontSize: 20, fontWeight: 700, color: theme.colors.warning }}>
                  {formatarMoeda((ficha.preco_venda_sugerido || 0) - (ficha.custo_unitario || 0))}
                </span>
              </div>
              <div style={{ ...t.section, textAlign: 'center' }}>
                <small style={{ color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Markup</small>
                <span style={{ fontSize: 20, fontWeight: 700, color: theme.colors.primary }}>
                  {ficha.custo_unitario > 0
                    ? `${(ficha.preco_venda_sugerido / ficha.custo_unitario).toFixed(2)}x`
                    : '-'}
                </span>
              </div>
              <div style={{ ...t.section, textAlign: 'center' }}>
                <small style={{ color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>CMV</small>
                <span style={{ fontSize: 20, fontWeight: 700, color: ficha.cmv_percentual > 35 ? theme.colors.danger : theme.colors.success }}>
                  {formatarPercentual(ficha.cmv_percentual)}
                </span>
                <small style={{ color: theme.colors.textMuted, display: 'block', fontSize: 11 }}>Ideal: 25-35%</small>
              </div>
              <div style={{ ...t.section, textAlign: 'center' }}>
                <small style={{ color: theme.colors.textMuted, display: 'block', marginBottom: 4 }}>Margem de Lucro</small>
                <span style={{ fontSize: 20, fontWeight: 700, color: theme.colors.info }}>{formatarPercentual(ficha.margem_lucro_percentual)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modo de Preparo */}
      {ficha.modo_preparo && (
        <div style={{ ...t.card, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', background: theme.colors.infoLight, borderBottom: `1px solid ${theme.colors.border}` }}>
            <h5 style={{ ...t.h3, color: theme.colors.info }}>👨‍🍳 Modo de Preparo</h5>
          </div>
          <div style={{ padding: 20 }}>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: 14, color: theme.colors.text }}>
              {ficha.modo_preparo}
            </div>
          </div>
        </div>
      )}

      {/* Botões de impressão */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        {/* Etiqueta da preparacao: nome, conservacao e validade ja vem da ficha,
            entao aqui e um toque. E o caminho mais curto entre "acabei de fazer"
            e o papel colado no pote. */}
        {temEtiquetas && (
          <button style={{ ...t.btnSecondary, borderColor: theme.colors.success, color: theme.colors.success }}
            onClick={() => setEtiquetaAberta(true)}>
            Imprimir etiqueta
          </button>
        )}
        <button style={t.btnSecondary} onClick={imprimir(exportarPDF)}>
          🖨️ Imprimir
        </button>
        <button style={{ ...t.btnSecondary, borderColor: theme.colors.primary, color: theme.colors.primary }} onClick={exportarPDF}>
          📄 Exportar PDF
        </button>
        <button style={{ ...t.btnSecondary, borderColor: theme.colors.success, color: theme.colors.success }} onClick={() => setMostrarReceita(true)}>
          📋 Receita Multiplicada
        </button>
      </div>

      {/* Modal Receita Multiplicada */}
      {mostrarReceita && (
        <div style={t.overlay} onClick={() => setMostrarReceita(false)}>
          <div style={{ ...t.modal, maxWidth: 900, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={t.h2}>📋 {ficha.nome} — Receita Multiplicada</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...t.btnSecondary, borderColor: theme.colors.info, color: theme.colors.info }} onClick={imprimir(exportarReceitaMultiplicadaPDF)}>
                  {isNative() ? '📄 PDF' : '🖨️ Imprimir'}
                </button>
                <button style={t.btnIcon} onClick={() => setMostrarReceita(false)}>✕</button>
              </div>
            </div>

            <div style={{ ...t.section, marginBottom: 16, textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
              Rendimento: {ficha.rendimento} {ficha.unidade_rendimento}{ficha.peso_total ? ` (${ficha.peso_total}g)` : ''}
            </div>

            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
              <table style={{ width: '100%', minWidth: 540, borderCollapse: 'collapse', fontSize: 13, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th style={{ ...t.th, textAlign: 'left', minWidth: 160, position: 'sticky', left: 0, background: theme.colors.bgCard, zIndex: 1 }}>Ingrediente</th>
                    {multiplicadores.map(m => (
                      <th key={m} style={{ ...t.th, textAlign: 'center', minWidth: 72 }}>{m === 0.5 ? '½×' : `${m}×`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {todosIngredientes.map((item, i) => (
                    <tr key={i} style={{ background: item.tipo === 'sub_receita' ? theme.colors.warningLight : undefined }}>
                      <td style={{ ...t.td, fontWeight: 600, minWidth: 160, position: 'sticky', left: 0, background: item.tipo === 'sub_receita' ? theme.colors.warningLight : theme.colors.bgCard, zIndex: 1, whiteSpace: 'normal' }}>{item.nome}</td>
                      {multiplicadores.map(m => {
                        const qtd = item.quantidade * m;
                        const fmt = qtd % 1 === 0 ? qtd : qtd.toFixed(2).replace('.', ',');
                        return <td key={m} style={{ ...t.td, textAlign: 'center' }}>{fmt} {item.unidade_medida}</td>;
                      })}
                    </tr>
                  ))}
                  {embalagensFicha.length > 0 && (
                    <>
                      <tr><td colSpan={multiplicadores.length + 1} style={{ ...t.td, textAlign: 'center', fontWeight: 700, background: theme.colors.bgSection, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: theme.colors.textMuted }}>📦 Embalagens</td></tr>
                      {embalagensFicha.map((item, i) => (
                        <tr key={`emb-${i}`}>
                          <td style={{ ...t.td, fontWeight: 600, position: 'sticky', left: 0, background: theme.colors.bgCard, zIndex: 1 }}>{item.nome}</td>
                          {multiplicadores.map(m => (
                            <td key={m} style={{ ...t.td, textAlign: 'center' }}>{Math.ceil(item.quantidade * m)}</td>
                          ))}
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {ficha.modo_preparo && (
              <div style={{ marginTop: 16, padding: 16, background: theme.colors.bgSection, borderRadius: 12, borderLeft: `4px solid ${theme.colors.primary}` }}>
                <h4 style={{ ...t.h3, color: theme.colors.primaryDark, marginBottom: 8 }}>👨‍🍳 Modo de Preparo</h4>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: 14, color: theme.colors.text }}>{ficha.modo_preparo}</div>
              </div>
            )}
          </div>
        </div>
      )}
      {etiquetaAberta && (
        <EtiquetaModal
          inicial={{ origem: 'ficha', ficha_tecnica_id: String(ficha.id), nome: ficha.nome,
                     conservacao: ficha.conservacao_padrao || 'refrigerado',
                     prazo_dias: ficha.validade_dias != null ? String(ficha.validade_dias) : '' }}
          onFechar={() => setEtiquetaAberta(false)}
        />
      )}
    </div>
  );
};

export default FichaTecnicaDetalhes;